import './video.component.scss';

import type { FunctionComponent } from 'preact';
import { useEffect, useMemo } from 'preact/hooks';
import { Provider } from 'react-redux';

import type { CustomerResponse } from '../../../auth/models/auth.model';
import { LegacyAuthModalState, LoginType } from '../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../auth/services/authentication-flow.service';
import {
  selectAccountSettings,
  selectAuthenticationLoaded,
  selectCustomer,
  selectIsAuthenticated,
} from '../../../auth/store/auth.selectors';
import {
  checkAuthentication,
  initializeCustomer,
  loadAccountSettings,
} from '../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { useTranslation } from '../../../i18n';
import {
  selectCreatingContract,
  selectJoinInProgress,
} from '../../../membership/store/membership.selectors';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { store } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  ContentAvailabilityResponseType,
  GatedAccessResponseType,
} from '../../models/access-response';
import type { Video } from '../../models/video.interface';
import { selectVideoById, selectVideosLoading } from '../../store/content.selectors';
import { loadVideo } from '../../store/content.thunk';
import { VideoInfo } from './video-info/video-info.component';
import { VideoOverlay } from './video-overlay/video-overlay.component';

export interface VideoComponentProps {
  videoId: string;
  maxWidth: boolean;
  display: 'always' | 'has_access' | 'no_access';
  showTitle: boolean;
  showArtist: boolean;
  showDescription: boolean;
  hideVideoInfoLoggedIn: boolean;
  showCountdown: boolean;
  freeOnlyButtonText: string;
  freePaidButtonText: string;
  paidOnlyButtonText: string;
  buttonColor: string;
  buttonTextColor: string;
  textColor: string;
  // CSS Variables
  borderWidth: string;
  borderRadius: string;
  borderColor: string;
  sectionBackgroundColor: string;
  topPadding: string;
  bottomPadding: string;
  horizontalPadding: string;
}

const VideoContent: FunctionComponent<VideoComponentProps> = ({
  videoId,
  maxWidth,
  display,
  showTitle,
  showArtist,
  showDescription,
  hideVideoInfoLoggedIn,
  showCountdown,
  borderWidth,
  borderRadius,
  borderColor,
  sectionBackgroundColor,
  topPadding,
  bottomPadding,
  horizontalPadding,
  freeOnlyButtonText,
  freePaidButtonText,
  paidOnlyButtonText,
  buttonColor,
  buttonTextColor,
  textColor,
}) => {
  const cssVars = {
    '--borderWidth': borderWidth,
    '--borderRadius': borderRadius,
    '--borderColor': borderColor,
    '--sectionBackgroundColor': sectionBackgroundColor,
    '--textColor': textColor,
    '--topPadding': topPadding,
    '--bottomPadding': bottomPadding,
    '--horizontalPadding': horizontalPadding,
  };

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading: boolean = useAppSelector(selectVideosLoading);
  const customer: CustomerResponse = useAppSelector(selectCustomer);
  const accountSettings: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const videoResp = useAppSelector((state) => selectVideoById(state, videoId));
  const { content: video } = videoResp || {};
  // A membership join is being processed (post-auth auto-join or explicit click).
  // Show a waiting state instead of the access lock so fans mid-join don't see
  // "You do not have access" while their contract is being created (SNGL-6967).
  const joinInProgress = useAppSelector(selectJoinInProgress);
  const creatingContract = useAppSelector(selectCreatingContract);
  const joiningMembership = joinInProgress || creatingContract;
  // Stable per mount: if these change on every render the iframe src changes too,
  // which tears down + re-injects the player and makes the poster / play button
  // flicker (and can leave it stuck on the poster) during the initial load.
  const sectionId = useMemo(() => crypto.randomUUID(), []);
  const cacheBust = useMemo(() => Date.now(), []);
  let videoPlayerUrl: string;
  let chatUrl: string;

  useEffect(() => {
    dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    dispatch(initializeCustomer());
    dispatch(loadAccountSettings());
  }, [dispatch]);

  const authLoaded = useAppSelector(selectAuthenticationLoaded);

  useEffect(() => {
    if (!authLoaded) return;
    authenticationFlowService.processStoredAuthenticationResult();
  }, [authLoaded]);

  const contentId = useMemo(() => {
    const fromProp = (videoId ?? '').trim();
    if (fromProp) return fromProp;

    const params = new URLSearchParams(window.location.search);
    return (params.get('content-id') ?? '').trim();
  }, [videoId, window.location.search]);

  useEffect(() => {
    if (!videoId) {
      return;
    }
    dispatch(loadVideo(videoId));
  }, [dispatch]);

  useEffect(() => {
    const videoListener = async (event: MessageEvent) => {
      if (event.data?.type === 'openChatModal') {
        if (event.data?.id === 'chat-login-modal') {
          await authenticationFlowService.initiateAuthentication({
            state: LegacyAuthModalState.LOGIN,
          });
        } else if (event.data?.id === 'chat-register-modal') {
          await authenticationFlowService.initiateAuthentication({
            state: LegacyAuthModalState.CREATE_ACCOUNT,
          });
        }
      }
    };

    window.addEventListener('message', videoListener, false);

    return () => {
      window.removeEventListener('message', videoListener, false);
    };
  }, []);

  const shouldDisplay = (): boolean => {
    return (
      !display ||
      display === 'always' ||
      (display === 'has_access' && isAuthenticated) ||
      (display === 'no_access' && !isAuthenticated)
    );
  };

  const shouldNotDisplay = (): boolean => !shouldDisplay();

  if (shouldNotDisplay()) return null;

  // Theme editor only: with no video configured, render a fully styled static
  // preview (color-bars placeholder + sample text + play button) so block style
  // edits are visible without uploading a video (SNGL-6956). Never shown on the
  // live storefront.
  const designMode = !!(window as any).Shopify?.designMode;
  if (!contentId && designMode) {
    const previewVideo = {
      id: 'preview',
      name: t('video.previewTitle'),
      artistName: t('video.previewArtist'),
      description: `<p>${t('video.previewDescription')}</p>`,
    } as Video;
    return (
      <div class="single-video-wrapper" style={cssVars}>
        <div class={`single-video-wrap ${maxWidth ? 'page-width no-padding' : ''}`}>
          <div class={`single-video ${maxWidth ? 'max-width' : ''}`}>
            <div class="single-video-editor-preview">
              <button type="button" class="single-preview-play-button" aria-label={t('a11y.play')}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m6 21 15-9L6 3v18Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        {(showTitle || showArtist || showDescription) && (
          <VideoInfo
            video={previewVideo}
            showTitle={showTitle}
            showArtist={showArtist}
            showDescription={showDescription}
          />
        )}
      </div>
    );
  }
  if (!contentId) return null;

  const hasAccess =
    videoResp?.accessResponse === GatedAccessResponseType.ACCESS_GRANTED ||
    videoResp?.availabilityResponse === ContentAvailabilityResponseType.UNRESTRICTED;

  if (videoResp && customer && accountSettings) {
    const injectIframe = (
      targetId: string,
      src: string,
      withRefresh: boolean,
      title: string
    ): boolean => {
      const target = document.getElementById(targetId);
      if (!target) {
        return false;
      }
      if (!target.querySelector(`iframe[src="${src}"]`)) {
        // src changed (customer/cache-bust) — drop any stale frame before injecting
        target.querySelectorAll('iframe').forEach((frame) => frame.remove());
        const iframe = document.createElement('iframe');
        iframe.id = `${targetId}-iframe`;
        iframe.title = title;
        iframe.classList.add('single-player');
        iframe.referrerPolicy = 'origin';
        iframe.src = src;
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.setAttribute('allowtransparency', 'yes');
        iframe.setAttribute('allow', 'autoplay; fullscreen');
        iframe.frameBorder = '0';
        iframe.marginHeight = '0';
        iframe.marginWidth = '0';
        iframe.scrolling = 'no';
        if (withRefresh) {
          iframe.addEventListener('load', () => {
            window.addEventListener(
              'message',
              (ev) => {
                if (ev.data === 'requestRefresh') {
                  const element = document.getElementById(
                    `${targetId}-iframe`
                  ) as HTMLIFrameElement;
                  const currentPage = new URL(element.src);
                  currentPage.searchParams.set('_', new Date().getTime().toString());
                  element.src = currentPage.toString();
                }
              },
              false
            );
          });
        }
        target.appendChild(iframe);
      }
      return true;
    };

    const buildPlayerParams = (): URLSearchParams => {
      const params = new URLSearchParams({
        id: video.id,
        customerEmail: !!customer?.email ? customer.email : '',
        sectionId: sectionId,
        _: `${cacheBust}`, // cache busting parameter (stable per mount)
      });

      if (!!customer?.customerId) {
        params.set('customerId', customer.customerId);
      }

      const bypassToken = new URLSearchParams(window.location.search).get('access-key');
      if (bypassToken) {
        params.set('token', bypassToken);
        params.set('soundcheck', 'true');
      }

      if (!accountSettings?.whiteLabel?.enabled) {
        params.set('watermark', 'true');
      }
      return params;
    };

    const setUpPlayers = (): void => {
      const host = `https://${video.videoHost}`;
      // Keep the video player a clean 16:9 (chatEnabled=false). When the video has
      // chat, render it as a separate /chat iframe so the storefront — not the
      // player's internal split — controls the layout (side panel / stacked).
      const videoParams = buildPlayerParams();
      videoParams.set('chatEnabled', 'false');
      videoPlayerUrl = `${host}/livestream?${videoParams.toString()}`;
      if (video.chatEnabled) {
        chatUrl = `${host}/chat?${buildPlayerParams().toString()}`;
      }
      const interval = setInterval(() => {
        const videoReady = injectIframe(
          `single-livestream-player-${sectionId}`,
          videoPlayerUrl,
          true,
          video.name || 'Video player'
        );
        const chatReady =
          !video.chatEnabled ||
          injectIframe(`single-chat-${sectionId}`, chatUrl, false, 'Live chat');
        if (videoReady && chatReady) {
          clearInterval(interval);
        }
      }, 250);
    };

    if (hasAccess) {
      setUpPlayers();
    }
  }

  const showVideoInfo = (): boolean => {
    return (
      !(isAuthenticated && hideVideoInfoLoggedIn) &&
      hasAccess &&
      (showTitle || showArtist || showDescription)
    );
  };

  const getVideoWrapClassName = () => {
    const classes = [];
    if (!hasAccess) classes.push('preview');
    if (maxWidth) classes.push('page-width');
    if (!isAuthenticated) classes.push('no-padding');
    return classes.join(' ');
  };

  return (
    <div
      class={`single-video-wrapper ${shouldNotDisplay() ? 'single-hidden' : ''}`}
      style={cssVars}
    >
      {loading && (
        <section className="single-loading">
          <SpinnerComponent />
        </section>
      )}

      {!loading && video && (
        <>
          <div class={`single-video-wrap ${getVideoWrapClassName()}`}>
            <div class={`single-video ${maxWidth ? 'max-width' : ''}`}>
              {!hasAccess && (
                <VideoOverlay
                  joining={joiningMembership}
                  video={video}
                  contentId={contentId}
                  showTitle={showTitle}
                  showArtist={showArtist}
                  showDescription={showDescription}
                  showCountdown={showCountdown}
                  freeOnlyButtonText={freeOnlyButtonText}
                  freePaidButtonText={freePaidButtonText}
                  paidOnlyButtonText={paidOnlyButtonText}
                  buttonColor={buttonColor}
                  buttonTextColor={buttonTextColor}
                  borderRadius={borderRadius}
                  borderWidth={borderWidth}
                  borderColor={borderColor}
                  textColor={textColor}
                />
              )}

              {hasAccess && (
                <div class={`single-video-player ${video.chatEnabled ? 'with-chat' : ''}`}>
                  <div id={`single-livestream-player-${sectionId}`} class="single-player-frame">
                    {/* ---------------------------------------------
                    | Video-only livestream iframe injected here
                    ----------------------------------------------- */}
                  </div>
                  {video.chatEnabled && (
                    <div id={`single-chat-${sectionId}`} class="single-chat-frame">
                      {/* Chat iframe (single-ui-livestream /chat) injected here */}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {showVideoInfo() && (
            <VideoInfo
              video={video}
              showTitle={showTitle}
              showArtist={showArtist}
              showDescription={showDescription}
            />
          )}
        </>
      )}
    </div>
  );
};

export const VideoComponent: FunctionComponent<VideoComponentProps> = (props) => {
  return (
    <Provider store={store}>
      <VideoContent {...props} />
    </Provider>
  );
};
